import { Injectable } from '@angular/core';
import { HistoryItemResponse } from '@shared/index';
import { AvatarImageService } from './avatar-image.service';

/** Hält den Nachrichtenverlauf des aktuellen Bot-Threads im Speicher. */
@Injectable({
  providedIn: 'root'
})
export class BotMessagesService {
  //Das bleibt privat
  private messages: HistoryItemResponse[] = [];

  constructor(
    private avatarimage: AvatarImageService
  ) { }

  /**
   * Gibt den kompletten Nachrichtenverlauf zurück.
   * @returns Alle Nachrichten des aktuellen Threads.
   */
  public getMessages(): HistoryItemResponse[] {
    return this.messages;
  }

  /**
   * Ersetzt den kompletten Nachrichtenverlauf.
   * @param messages Die neuen Nachrichten.
   */
  public setMessages(messages: HistoryItemResponse[]) {
    void this.avatarimage.loadAvatarUrls();
    this.messages = messages;
  }

  /**
   * Hängt eine Nachricht an den Verlauf an.
   * @param message Die anzuhängende Nachricht.
   */
  public addMessage(message: HistoryItemResponse) {
    void this.avatarimage.loadAvatarUrls();
    this.messages.push(message);
  }

  /**
   * Hängt eine gestreamte (Run-)Nachricht als fertige Text-Nachricht an den Verlauf an.
   * @param message Das Run-Delta-Objekt mit dem finalen Textinhalt.
   */
  public addRunMessage(message: Omit<HistoryItemResponse, "content"> & { snapshot: string; deltaStr: string; }) {
    void this.avatarimage.loadAvatarUrls();
    this.messages.push({
      item_id: message.item_id,
      _id: message._id,
      context: message.context,
      created: message.created,
      memoryEnabled: message.memoryEnabled,
      role: message.role,
      supervisorEnabled: message.supervisorEnabled,
      threadId: message.threadId,
      bypass: message.bypass,
      content: [{ text: message.deltaStr, type: 'text' }],
      reasoning: message.reasoning ?? ""
    });
  }

  /**
   * Gibt die Anzahl der Nachrichten im Verlauf zurück.
   * @returns Anzahl der Nachrichten.
   */
  public getMessageCount() {
    return this.messages.length;
  }

  /**
   * Gibt die Nachricht an der angegebenen Position zurück.
   * @param index Position im Nachrichtenverlauf.
   * @returns Die Nachricht an dieser Position.
   */
  public getMessage(index: number) {
    return this.messages[index];
  }

  /**
   * Ändert den Textinhalt einer bestehenden Nachricht.
   * @param index Position der zu ändernden Nachricht.
   * @param content Der neue Textinhalt.
   */
  public updateContent(index: number, content: string) {
    if (this.messages[index]?.content[0] && this.messages[index].content[0].type === 'text') {
      this.messages[index].content[0].text = content;
    }
  }

  /**
   * Findet die Position einer Nachricht anhand ihrer ID.
   * @param id ID der gesuchten Nachricht.
   * @returns Die Position, oder `-1` wenn nicht gefunden.
   */
  public findMessageIndex(id: string) {
    return this.messages.findIndex(m => m._id === id);
  }

  /**
   * Entfernt eine Nachricht aus dem Verlauf.
   * @param id ID der zu entfernenden Nachricht.
   */
  public deleteMessage(id: string) {
    this.messages = this.messages.filter(m => m._id !== id);
  }

  /**
   * Entfernt mehrere Nachrichten aus dem Verlauf.
   * @param ids IDs der zu entfernenden Nachrichten.
   */
  public deleteMessages(ids: string[]) {
    this.messages = this.messages.filter(m => !ids.includes(m._id));
  }

  /**
   * Gibt die zuletzt vom Bot gesendete Nachricht zurück.
   * @returns Die letzte Assistant-Nachricht, oder `undefined`.
   */
  public findLatestAssistantMessage() {
    return this.messages.findLast(msg => msg.role === 'assistant');
  }
}
